import React, { useState, useEffect } from 'react';
import type { FC, ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Switch,
  Divider,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Checkbox,
  useTheme,
  Stack,
  Select,
  Chip,
  OutlinedInput,
  ListItemText,
  SelectChangeEvent
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VpnKey as VpnKeyIcon,
  KeyboardBackspace as KeyboardBackspaceIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { User, UserFormData, BranchRef, AccountRef } from '../../types/user';
import { UserGroup } from '../../types/userGroup';
import { usersApi } from '../../services/api';
import { userGroupsApi } from '../../services/userGroupService';
import { getAccounts, Account } from '../../services/financeService';

interface UserFormProps {
  mode: 'create' | 'edit';
}

const UserForm: FC<UserFormProps> = ({ mode }): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Initial form data
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: mode === 'create' ? '' : undefined,
    fullName: '',
    phone: '',
    status: 'active',
    accessibleBranches: [],
    defaultBranch: '',
    userGroup: '',
    isSystemAdmin: false,
    accessibleAccounts: []
  });
  
  const [branches, setBranches] = useState<Array<{_id: string, name: string}>>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchUserData = async () => {
      if (mode === 'edit' && id) {
        try {
          const userData = await usersApi.getUser(id);
          
          // Map the API user data to form data structure
          setFormData({
            username: userData.username,
            email: userData.email,
            password: undefined, // Don't set password in edit mode
            fullName: userData.fullName,
            phone: userData.phone || '',
            status: userData.status,
            accessibleBranches: userData.accessibleBranches?.map(branch => branch._id) || [],
            defaultBranch: userData.defaultBranch?._id || '',
            userGroup: userData.userGroup?._id || '',
            isSystemAdmin: userData.isSystemAdmin || false,
            accessibleAccounts: userData.accessibleAccounts?.filter(account => account != null).map(account => account._id) || []
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          setShowAlert({
            type: 'error',
            message: 'فشل في تحميل بيانات المستخدم. يرجى المحاولة مرة أخرى.'
          });
        }
      }
    };
    
    fetchUserData();
  }, [id, mode]);
  
  // Fetch branches, accounts and user groups
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        // Replace with your actual branch API
        const branchesData = await usersApi.getBranches();
        setBranches(branchesData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };
    
    const fetchAccounts = async () => {
      try {
        // جلب الحسابات المالية النشطة فقط
        const accountsData = await getAccounts({ isActive: true });
        setAccounts(accountsData);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    
    // تحميل مجموعات المستخدمين
    const fetchUserGroups = async () => {
      try {
        const response = await userGroupsApi.getUserGroups(1, 100, '', 'active');
        setUserGroups(response.userGroups);
      } catch (error) {
        console.error('Error fetching user groups:', error);
      }
    };
    
    fetchBranches();
    fetchAccounts();
    fetchUserGroups();
  }, []);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleAccountsChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const selectedAccounts = typeof value === 'string' ? value.split(',') : value;
    
    setFormData(prev => ({ ...prev, accessibleAccounts: selectedAccounts }));
  };
  
  const handleBranchesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const branches = typeof value === 'string' ? value.split(',') : value;
    
    setFormData(prev => ({ ...prev, accessibleBranches: branches }));
    
    // If default branch is not in the selected branches, reset it
    if (formData.defaultBranch && !branches.includes(formData.defaultBranch)) {
      setFormData(prev => ({ ...prev, defaultBranch: '' }));
    }
  };
  
  const handleDefaultBranchChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, defaultBranch: value }));
  };
  
  const handleUserGroupChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, userGroup: value }));
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'اسم المستخدم مطلوب';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'صيغة البريد الإلكتروني غير صحيحة';
    }
    
    if (mode === 'create' && !formData.password) {
      newErrors.password = 'كلمة المرور مطلوبة';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'الاسم الكامل مطلوب';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setIsSubmitting(true);
    setShowAlert(null);
    
    try {
      // Make a copy of formData
      const cleanedFormData = {
        ...formData
      };
      
      console.log('Submitting user data:', JSON.stringify(cleanedFormData, null, 2));
      
      if (mode === 'create') {
        await usersApi.createUser(cleanedFormData);
        setShowAlert({
          type: 'success',
          message: 'تم إنشاء المستخدم بنجاح'
        });
        
        // Reset form after successful creation
        setFormData({
          username: '',
          email: '',
          password: '',
          fullName: '',
          phone: '',
          status: 'active',
          accessibleBranches: [],
          defaultBranch: '',
          userGroup: '',
          isSystemAdmin: false,
          accessibleAccounts: []
        });
        
        // Redirect to user list
        navigate('/users');
      } else if (id) {
        await usersApi.updateUser(id, cleanedFormData);
        setShowAlert({
          type: 'success',
          message: 'تم تحديث المستخدم بنجاح'
        });
        
        // Stay on edit page or redirect
        navigate('/users');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      let errorMessage = 'حدث خطأ أثناء حفظ البيانات. الرجاء التحقق من المعلومات المدخلة والمحاولة مرة أخرى.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        console.error('Error details:', error.stack);
      }
      
      setShowAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: 'grey.50', 
        py: 4, 
        px: { xs: 2, md: 4 } 
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          mb: 4,
          boxShadow: theme.shadows[2],
          maxWidth: 1200,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative header bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            bgcolor: 'secondary.main',
            background: 'linear-gradient(to right, #9c27b0, #673ab7)'
          }}
        />
        
        {/* Content */}
        <Box sx={{ pt: 2 }}>
          {/* Header with back button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <PersonIcon color="secondary" fontSize="large" />
              {mode === 'create' ? 'إضافة مستخدم جديد' : 'تعديل بيانات المستخدم'}
            </Typography>
            
            <Button
              startIcon={<KeyboardBackspaceIcon />}
              onClick={() => navigate('/users')}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'rgba(0, 0, 0, 0.02)'
                }
              }}
            >
              العودة للقائمة
            </Button>
          </Box>
          
          {showAlert && (
            <Alert 
              severity={showAlert.type} 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setShowAlert(null)}
            >
              {showAlert.message}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Card
              elevation={0}
              sx={{
                mb: 4,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ pb: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    mb: 3,
                    color: 'text.primary',
                    fontWeight: 600,
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      width: 40,
                      height: 3,
                      background: theme.palette.secondary.main,
                      bottom: -8,
                      right: 0,
                      borderRadius: 1
                    }
                  }}
                >
                  معلومات الحساب
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Username */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="اسم المستخدم"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.username}
                      helperText={errors.username}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  
                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="البريد الإلكتروني"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.email}
                      helperText={errors.email}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  
                  {/* Password */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={mode === 'create' ? 'كلمة المرور' : 'كلمة المرور (اتركها فارغة للحفاظ على القديمة)'}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password || ''}
                      onChange={handleChange}
                      fullWidth
                      required={mode === 'create'}
                      error={!!errors.password}
                      helperText={errors.password}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  
                  {/* Full Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="الاسم الكامل"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      fullWidth
                      required
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      variant="outlined"
                      InputProps={{
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  
                  {/* Phone */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="رقم الهاتف"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  
                  {/* Status */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="حالة الحساب"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="outlined"
                      SelectProps={{ 
                        MenuProps: { 
                          PaperProps: { 
                            sx: { maxHeight: 300 } 
                          } 
                        } 
                      }}
                      InputProps={{
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      <MenuItem value="active">نشط</MenuItem>
                      <MenuItem value="inactive">غير نشط</MenuItem>
                    </TextField>
                  </Grid>
                  {/* مدير النظام - تجاوز الصلاحيات */}
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset" sx={{ m: 1 }}>
                      <FormLabel component="legend" sx={{
                        color: 'text.primary',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}>
                        خاصية مدير النظام
                      </FormLabel>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.isSystemAdmin || false}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  isSystemAdmin: e.target.checked
                                }));
                              }}
                              name="isSystemAdmin"
                              color="secondary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                مدير النظام
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                تمنح المستخدم صلاحية تجاوز جميع صلاحيات النظام
                              </Typography>
                            </Box>
                          }
                        />
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  
                  {/* User Group - مجموعة المستخدمين للصلاحيات */}
                  {/* User Group - مجموعة المستخدمين للصلاحيات */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Typography
                        component="label"
                        htmlFor="user-group"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <PersonIcon color="action" fontSize="small" />
                        مجموعة المستخدم للصلاحيات
                      </Typography>
                      <Select
                        id="user-group"
                        value={formData.userGroup || ''}
                        onChange={handleUserGroupChange}
                        displayEmpty
                        input={<OutlinedInput sx={{ borderRadius: 2 }} />}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>لم يتم تحديد مجموعة</em>
                        </MenuItem>
                        {userGroups.map((group) => (
                          <MenuItem key={group._id} value={group._id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Accessible Branches - Multi-select */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Typography
                        component="label"
                        htmlFor="accessible-branches"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <BusinessIcon color="action" fontSize="small" />
                        الفروع المتاحة للمستخدم
                      </Typography>
                      <Select
                        id="accessible-branches"
                        multiple
                        value={formData.accessibleBranches || []}
                        onChange={handleBranchesChange}
                        input={<OutlinedInput sx={{ borderRadius: 2 }} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const branch = branches.find(b => b._id === value);
                              return (
                                <Chip
                                  key={value}
                                  label={branch?.name || value}
                                  size="small"
                                  sx={{ borderRadius: 1 }}
                                />
                              );
                            })}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        {branches.map((branch) => (
                          <MenuItem key={branch._id} value={branch._id}>
                            <Checkbox checked={(formData.accessibleBranches || []).indexOf(branch._id) > -1} />
                            <ListItemText primary={branch.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Accessible Accounts - Multi-select for Account Statements */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Typography
                        component="label"
                        htmlFor="accessible-accounts"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AttachMoneyIcon color="action" fontSize="small" />
                        الحسابات المتاحة للمستخدم في كشف الحساب
                      </Typography>
                      <Select
                        id="accessible-accounts"
                        multiple
                        value={formData.accessibleAccounts || []}
                        onChange={handleAccountsChange}
                        input={<OutlinedInput sx={{ borderRadius: 2 }} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              // البحث عن الحساب بالمعرف، سواء كان المعرف بصيغة id أو _id
                              const account = accounts.find(a => a.id === value || (a as any)._id === value);
                              return (
                                <Chip
                                  key={value}
                                  label={account ? `${account.accountNumber} - ${account.name}` : value}
                                  size="small"
                                  sx={{ borderRadius: 1 }}
                                />
                              );
                            })}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        {accounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            <Checkbox checked={(formData.accessibleAccounts || []).indexOf(account.id) > -1} />
                            <ListItemText primary={`${account.accountNumber} - ${account.name}`} />
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        اختر الحسابات التي يمكن للمستخدم عرض كشف حسابها في صفحة كشف الحساب
                      </Typography>
                    </FormControl>
                  </Grid>
                  
                  {/* Default Branch - Single select from accessible branches */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!formData.accessibleBranches?.length}>
                      <Typography
                        component="label"
                        htmlFor="default-branch"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <BusinessIcon color="primary" fontSize="small" />
                        الفرع الافتراضي
                      </Typography>
                      <Select
                        id="default-branch"
                        value={formData.defaultBranch || ''}
                        onChange={handleDefaultBranchChange}
                        displayEmpty
                        input={<OutlinedInput sx={{ borderRadius: 2 }} />}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>لم يتم تحديد فرع افتراضي</em>
                        </MenuItem>
                        {branches
                          .filter(branch => formData.accessibleBranches?.includes(branch._id))
                          .map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name}
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  px: 5,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'medium'
                }}
              >
                {isSubmitting ? 'جاري الحفظ...' : 'حفظ البيانات'}
              </Button>
              
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/users')}
                sx={{
                  px: 5,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'medium'
                }}
              >
                إلغاء
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserForm;